import type { ApiClient } from "@src/packages/api/louise-api";
import type { paths } from "@src/packages/openapi/schema";

type DocumentProcessingRunLookupResponse =
  paths["/patients/{patientId}/upload-jobs/{jobId}/items/{fileItemId}/document-processing-run"]["get"]["responses"][200]["content"]["application/json"]["data"];

export type DocumentProcessingRun = NonNullable<
  DocumentProcessingRunLookupResponse["run"]
>;
export type DocumentProcessingClaim = DocumentProcessingRun["claims"][number];
export type DocumentProcessingParty = DocumentProcessingRun["parties"][number];
export type DocumentProcessingRunLookup = DocumentProcessingRunLookupResponse;
export type DocumentProcessingPrefillMedication = {
  medicationName: string;
  dosage: string;
  dosageUnit: string;
  frequency: string;
  route: string;
  deliveryMethod: string;
  instructions: string;
  startDate: string;
  endDate: string;
  duration: string;
  timeOfDay: string;
};
export type DocumentProcessingPrefillAppointment = {
  title: string;
  type: string;
  date: string;
  time: string;
  endDate: string;
  endTime: string;
  durationMinutes: number | null;
  location: string;
  practitioner: string;
  notes: string;
};
export type DocumentProcessingPrefillResult = {
  marker: string;
  value: string;
  unit: string;
  referenceRange: string;
};
export type DocumentProcessingPrefillInsurance = {
  payerName: string;
  memberId: string;
  groupNumber: string;
  subscriberName: string;
  streetAddress: string;
  city: string;
  postalCode: string;
};
export type DocumentProcessingPrefillQuote = {
  clinicName: string;
  quoteDate: string;
  currency: string;
  totalAmount: string;
  lineItems: {
    label: string;
    amount: string;
    category: string;
    notes: string;
  }[];
};
export type DocumentProcessingPrefillSpecimen = {
  specimenType: string;
  clinicName: string;
  country: string;
  retrievalDate: string;
  freezeDate: string;
  freezingMethod: string;
  numberFrozen: number | null;
  totalRetrieved: number | null;
  matureMII: number | null;
  numberFertilized: number | null;
  collectedAt: string;
  collectionMethod: string;
  processingMethod: string;
  abstinencePeriod: number | null;
  concentration: number | null;
  totalCount: number | null;
  motilityPercent: number | null;
  morphologyPercent: number | null;
  volumeMl: number | null;
  cohortDisposition: string;
  dayOfDevelopment: string;
  fertilisationMethod: string;
  pgtPerformed: string;
  totalEmbryosCreated: number | null;
  numberTested: number | null;
  numberEuploid: number | null;
  numberAneuploid: number | null;
  numberMosaic: number | null;
  numberNoResult: number | null;
  storageId: string;
  embryos: {
    embryoId: string;
    dayOfDevelopment: string;
    grade: string;
    geneticStatus: string;
    sex: string;
    status: string;
  }[];
};
export type DocumentProcessingPrefill = {
  documentTitle: string;
  documentDate: string;
  providerName: string;
  prescriber: string;
  clinicName: string;
  labName: string;
  insurerName: string;
  claims: DocumentProcessingClaim[];
  parties: DocumentProcessingParty[];
  completeness: DocumentProcessingRun["completeness"];
  omittedReason: string | null;
  medications?: DocumentProcessingPrefillMedication[];
  appointments?: DocumentProcessingPrefillAppointment[];
  results?: DocumentProcessingPrefillResult[];
  insurance: DocumentProcessingPrefillInsurance;
  quote: DocumentProcessingPrefillQuote;
  specimen: DocumentProcessingPrefillSpecimen;
  documentLabel: string;
  summary: string;
};

const PROCESSING_RUN_LOOKUP_PATH =
  "/patients/{patientId}/upload-jobs/{jobId}/items/{fileItemId}/document-processing-run";

function processingRunError(response: { error?: unknown }) {
  if (response.error && typeof response.error === "object") {
    const message = (response.error as { message?: unknown }).message;
    if (typeof message === "string" && message.length > 0) {
      return new Error(message);
    }
  }
  return new Error("Could not read document processing progress");
}

export async function getDocumentProcessingRunForUploadJobItem(
  client: ApiClient,
  patientId: string,
  jobId: string,
  fileItemId: string,
): Promise<DocumentProcessingRunLookup> {
  const { data, error } = await client.GET(PROCESSING_RUN_LOOKUP_PATH, {
    params: { path: { patientId, jobId, fileItemId } },
  });
  if (!data?.data) throw processingRunError({ error });
  return data.data;
}

const TERMINAL_RUN_STATUSES = new Set(["succeeded", "failed", "cancelled"]);

export type DocumentProcessingOutcome = {
  lookup: DocumentProcessingRunLookup;
  run: DocumentProcessingRun;
};

export async function waitForDocumentProcessingOutcome(
  client: ApiClient,
  patientId: string,
  jobId: string,
  fileItemId: string,
  options: { attempts?: number; intervalMs?: number } = {},
): Promise<DocumentProcessingOutcome | null> {
  const attempts = options.attempts ?? 12;
  const intervalMs = options.intervalMs ?? 1_250;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const lookup = await getDocumentProcessingRunForUploadJobItem(
      client,
      patientId,
      jobId,
      fileItemId,
    );
    if (
      lookup.state === "ready" &&
      lookup.run &&
      TERMINAL_RUN_STATUSES.has(lookup.run.status)
    ) {
      return { lookup, run: lookup.run };
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  return null;
}

export async function waitForDocumentProcessingRun(
  client: ApiClient,
  patientId: string,
  jobId: string,
  fileItemId: string,
  options: { attempts?: number; intervalMs?: number } = {},
): Promise<DocumentProcessingRun | null> {
  const outcome = await waitForDocumentProcessingOutcome(
    client,
    patientId,
    jobId,
    fileItemId,
    options,
  );
  return outcome?.run ?? null;
}

export async function waitForDocumentSourceIdentity(
  client: ApiClient,
  patientId: string,
  jobId: string,
  fileItemId: string,
  options: { attempts?: number; intervalMs?: number } = {},
): Promise<{
  patientDocumentId: string;
  sourceEntityId: string | null;
}> {
  const attempts = options.attempts ?? 12;
  const intervalMs = options.intervalMs ?? 1_250;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const lookup = await getDocumentProcessingRunForUploadJobItem(
      client,
      patientId,
      jobId,
      fileItemId,
    );
    if (lookup.patientDocumentId) {
      return {
        patientDocumentId: lookup.patientDocumentId,
        sourceEntityId: lookup.sourceEntityId,
      };
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  throw new Error("The canonical document source was not saved in time");
}

function claimText(claim: DocumentProcessingClaim): string {
  if (claim.rawValue.trim()) return claim.rawValue.trim();
  const proposal = claim.normalizedValue?.proposal;
  return typeof proposal === "string" ? proposal.trim() : "";
}

function firstClaimValue(
  claims: readonly DocumentProcessingClaim[],
  patterns: readonly RegExp[],
): string {
  const claim = claims.find((candidate) =>
    patterns.some((pattern) => pattern.test(candidate.fieldPath)),
  );
  return claim ? claimText(claim) : "";
}

function claimGroups(
  claims: readonly DocumentProcessingClaim[],
  pattern: RegExp,
): Map<string, DocumentProcessingClaim[]> {
  const groups = new Map<string, DocumentProcessingClaim[]>();
  for (const claim of claims) {
    const match = pattern.exec(claim.fieldPath);
    if (!match) continue;
    const group = groups.get(match[1] ?? "0") ?? [];
    group.push(claim);
    groups.set(match[1] ?? "0", group);
  }
  return groups;
}

function groupedValue(
  claims: readonly DocumentProcessingClaim[],
  patterns: readonly RegExp[],
): string {
  const claim = claims.find((candidate) =>
    patterns.some((pattern) => pattern.test(candidate.fieldPath)),
  );
  return claim ? claimText(claim) : "";
}

function canonicalDateValue(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !value.startsWith("0000") &&
    !value.startsWith("0001")
    ? value
    : "";
}

function numericClaimValue(
  claims: readonly DocumentProcessingClaim[],
  patterns: readonly RegExp[],
): number | null {
  const raw = firstClaimValue(claims, patterns).replace(/,/g, "");
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Adapts canonical claims to the existing contextual form model. This is a
 * prefill projection only: the complete run remains available in the API and
 * no claim is discarded because it has no form field.
 */
export function projectDocumentProcessingRunToPrefill(
  run: DocumentProcessingRun,
  sourceFileName: string,
): DocumentProcessingPrefill {
  const claims = run.claims;
  const physician = run.parties.find((party) => party.role === "physician");
  const clinic = run.parties.find((party) =>
    ["clinic", "facility"].includes(party.role),
  );
  const laboratory = run.parties.find((party) => party.role === "laboratory");
  const payer = run.parties.find((party) =>
    ["payer", "issuer"].includes(party.role),
  );
  const title =
    firstClaimValue(claims, [
      /title/i,
      /report[_\. ]?name/i,
      /document[_\. ]?name/i,
    ]) || sourceFileName.replace(/\.[^.]+$/, "");
  const dateClaim = claims.find(
    (claim) => claim.kind === "date" || /date/i.test(claim.fieldPath),
  );
  const candidateDate = dateClaim?.rawDate?.trim() ?? "";
  const date = canonicalDateValue(candidateDate);
  const data: DocumentProcessingPrefill = {
    documentTitle: title,
    documentDate: date,
    documentLabel: "",
    summary: "",
    providerName:
      physician?.normalizedName ||
      physician?.rawName ||
      clinic?.normalizedName ||
      clinic?.rawName ||
      laboratory?.normalizedName ||
      laboratory?.rawName ||
      "",
    prescriber: physician?.normalizedName || physician?.rawName || "",
    clinicName: clinic?.normalizedName || clinic?.rawName || "",
    labName: laboratory?.normalizedName || laboratory?.rawName || "",
    insurerName: payer?.normalizedName || payer?.rawName || "",
    claims,
    parties: run.parties,
    completeness: run.completeness,
    omittedReason: run.omittedReason,
    insurance: {
      payerName: firstClaimValue(claims, [
        /payer/i,
        /insurer/i,
        /insurance[_\. ]?company/i,
      ]),
      memberId: firstClaimValue(claims, [
        /member[_\. ]?id/i,
        /subscriber[_\. ]?id/i,
      ]),
      groupNumber: firstClaimValue(claims, [/group[_\. ]?(?:number|id)/i]),
      subscriberName: firstClaimValue(claims, [
        /subscriber[_\. ]?name/i,
        /policyholder/i,
      ]),
      streetAddress: firstClaimValue(claims, [
        /street[_\. ]?address/i,
        /address/i,
      ]),
      city: firstClaimValue(claims, [/city/i]),
      postalCode: firstClaimValue(claims, [/postal[_\. ]?code/i, /zip/i]),
    },
    quote: {
      clinicName: firstClaimValue(claims, [/clinic[_\. ]?name/i, /facility/i]),
      quoteDate: canonicalDateValue(
        firstClaimValue(claims, [/quote[_\. ]?date/i]),
      ),
      currency: firstClaimValue(claims, [/currency/i]),
      totalAmount: firstClaimValue(claims, [/total[_\. ]?amount/i, /total/i]),
      lineItems: [],
    },
    specimen: {
      specimenType: firstClaimValue(claims, [/specimen[_\. ]?type/i]),
      clinicName: firstClaimValue(claims, [/clinic[_\. ]?name/i, /facility/i]),
      country: firstClaimValue(claims, [/country/i]),
      retrievalDate: canonicalDateValue(
        firstClaimValue(claims, [/retrieval[_\. ]?date/i]),
      ),
      freezeDate: canonicalDateValue(
        firstClaimValue(claims, [/freeze[_\. ]?date/i]),
      ),
      freezingMethod: firstClaimValue(claims, [/freezing[_\. ]?method/i]),
      numberFrozen: numericClaimValue(claims, [/number[_\. ]?frozen/i]),
      totalRetrieved: numericClaimValue(claims, [/total[_\. ]?retrieved/i]),
      matureMII: numericClaimValue(claims, [/mature[_\. ]?mii/i]),
      numberFertilized: numericClaimValue(claims, [
        /number[_\. ]?fertili[sz]ed/i,
      ]),
      collectedAt: canonicalDateValue(
        firstClaimValue(claims, [
          /collected[_\. ]?at/i,
          /collection[_\. ]?date/i,
        ]),
      ),
      collectionMethod: firstClaimValue(claims, [/collection[_\. ]?method/i]),
      processingMethod: firstClaimValue(claims, [/processing[_\. ]?method/i]),
      abstinencePeriod: numericClaimValue(claims, [/abstinence[_\. ]?period/i]),
      concentration: numericClaimValue(claims, [/concentration/i]),
      totalCount: numericClaimValue(claims, [/total[_\. ]?count/i]),
      motilityPercent: numericClaimValue(claims, [/motility/i]),
      morphologyPercent: numericClaimValue(claims, [/morphology/i]),
      volumeMl: numericClaimValue(claims, [/volume/i]),
      cohortDisposition: firstClaimValue(claims, [/cohort[_\. ]?disposition/i]),
      dayOfDevelopment: firstClaimValue(claims, [
        /day[_\. ]?of[_\. ]?development/i,
      ]),
      fertilisationMethod: firstClaimValue(claims, [
        /fertili[sz]ation[_\. ]?method/i,
      ]),
      pgtPerformed: firstClaimValue(claims, [/pgt[_\. ]?performed/i]),
      totalEmbryosCreated: numericClaimValue(claims, [/total[_\. ]?embryos/i]),
      numberTested: numericClaimValue(claims, [/number[_\. ]?tested/i]),
      numberEuploid: numericClaimValue(claims, [/number[_\. ]?euploid/i]),
      numberAneuploid: numericClaimValue(claims, [/number[_\. ]?aneuploid/i]),
      numberMosaic: numericClaimValue(claims, [/number[_\. ]?mosaic/i]),
      numberNoResult: numericClaimValue(claims, [
        /number[_\. ]?no[_\. ]?result/i,
      ]),
      storageId: firstClaimValue(claims, [/storage[_\. ]?(?:id|location)/i]),
      embryos: [],
    },
  };

  const medicationGroups = claimGroups(
    claims,
    /(?:^|\.)(?:medications?|prescriptions?)(?:\[(\d+)\])?(?:\.|$)/i,
  );
  const medications = Array.from(medicationGroups.values()).map((group) => ({
    medicationName: groupedValue(group, [
      /\.name$/i,
      /(?:^|[._])(?:medication|drug|medicine)(?:name)?$/i,
    ]),
    dosage: groupedValue(group, [/dose|dosage|strength/i]),
    dosageUnit: groupedValue(group, [/unit/i]),
    frequency: groupedValue(group, [/frequency|schedule/i]),
    route: groupedValue(group, [/route/i]),
    deliveryMethod: groupedValue(group, [/form|delivery/i]),
    instructions: groupedValue(group, [/instruction|direction|sig/i]),
    startDate: canonicalDateValue(groupedValue(group, [/start[_\. ]?date/i])),
    endDate: canonicalDateValue(groupedValue(group, [/end[_\. ]?date/i])),
    duration: groupedValue(group, [/duration/i]),
    timeOfDay: groupedValue(group, [/time/i]),
  }));
  if (medications.length > 0) data.medications = medications;

  const appointmentGroups = claimGroups(
    claims,
    /(?:appointments?|visits?|referrals?)\[(\d+)\]/i,
  );
  const appointments = Array.from(appointmentGroups.values()).map((group) => ({
    title: groupedValue(group, [/\.title$/i, /name/i]),
    type: groupedValue(group, [/type|kind/i]),
    date: canonicalDateValue(groupedValue(group, [/date/i])),
    time: groupedValue(group, [/time/i]),
    endDate: canonicalDateValue(groupedValue(group, [/end[_\. ]?date/i])),
    endTime: groupedValue(group, [/end[_\. ]?time/i]),
    durationMinutes: (() => {
      const raw = groupedValue(group, [/duration/i]);
      const parsed = Number(raw);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    })(),
    location: groupedValue(group, [/location|address|facility/i]),
    practitioner: groupedValue(group, [/practitioner|physician|doctor/i]),
    notes: groupedValue(group, [/note|instruction/i]),
  }));
  if (appointments.length > 0) data.appointments = appointments;

  const resultGroups = claimGroups(
    claims,
    /(?:results?|labs?|markers?)\[(\d+)\]/i,
  );
  const results = Array.from(resultGroups.values()).map((group) => ({
    marker: groupedValue(group, [/marker|name|label/i]),
    value: groupedValue(group, [/value|result/i]),
    unit: groupedValue(group, [/unit/i]),
    referenceRange: groupedValue(group, [/reference|range/i]),
  }));
  if (results.length > 0) data.results = results;

  const lineItemGroups = claimGroups(
    claims,
    /(?:line[_\. ]?items?|charges?|fees?)\[(\d+)\]/i,
  );
  data.quote.lineItems = Array.from(lineItemGroups.values()).map((group) => ({
    label: groupedValue(group, [/label|name|description/i]),
    amount: groupedValue(group, [/amount|price|cost|total/i]),
    category: groupedValue(group, [/category|type/i]),
    notes: groupedValue(group, [/note|instruction/i]),
  }));

  const embryoGroups = claimGroups(claims, /embryos?\[(\d+)\]/i);
  data.specimen.embryos = Array.from(embryoGroups.values()).map((group) => ({
    embryoId: groupedValue(group, [/embryo[_\. ]?id/i, /id/i]),
    dayOfDevelopment: groupedValue(group, [/day[_\. ]?of[_\. ]?development/i]),
    grade: groupedValue(group, [/grade/i]),
    geneticStatus: groupedValue(group, [/genetic[_\. ]?status/i]),
    sex: groupedValue(group, [/sex/i]),
    status: groupedValue(group, [/status/i]),
  }));

  const classification = run.classificationCode ?? "other";
  data.documentLabel =
    classification === "lab_result"
      ? "lab"
      : classification === "imaging_result"
        ? "imaging"
        : classification === "prescription" || classification === "medication"
          ? "treatment_plan"
          : classification === "appointment"
            ? "appointment_letter"
            : "other";
  data.summary = run.segments.map((segment) => segment.rawText).join("\n\n");
  return data;
}
